import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Authorization } from "../../Context/Authorization";
import Preloader from "../../shared/PreLoaders";
import { AuthContext } from "../../Context/AuthContext";

const ManageShgMember = () => {
  const [shgMemberData, setShgMemberData] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    if (!selectedJfmcType) return;
    fetchShgMember(selectedJfmcType._id);
  }, [selectedJfmcType]);

  const fetchShgMember = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const config = Authorization();
      const res = await axios.post(`${Api}/api/shgMember/by-jfmcType`, { jfmcTypeId }, config);
      setShgMemberData(res.data.shgMembers || []);
    } catch (e) {
      console.error("Error fetching members", e);
    } finally {
      setLoading(false);
    }
  };

  const deleteShgMembers = async (id) => {
    if (!window.confirm("Are you sure you want to delete this SHG member?")) return;
    try {
      const config = Authorization();
      await axios.delete(`${Api}/api/shgMember/${id}`, config);
      setShgMemberData((p) => p.filter((m) => m._id !== id));
    } catch (e) {
      console.error("Error deleting member", e);
    }
  };

  // Group by SHG
  const groupedShgMembers = Object.values(
    shgMemberData.reduce((acc, sm) => {
      const shgId = sm.shg?._id?.toString();
      if (!shgId) return acc;
      if (!acc[shgId]) acc[shgId] = { shg: sm.shg, members: [] };
      acc[shgId].members.push(sm);
      return acc;
    }, {})
  );

  if (!selectedJfmcType) {
    return (
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
        <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>No JFMC Type Selected</div>
        <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
      </div>
    );
  }

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* Page Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Manage SHG Members</h4>
          <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
            <span style={{ color: "#A0AEC0" }}>SHG</span>
            <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
            <span style={{ color: "#2E7D32", fontWeight: 600 }}>All SHG Members</span>
          </div>
        </div>
        <button
          onClick={() => navigate("/SHG/join")}
          style={{ padding: "9px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 7, boxShadow: "0 2px 8px rgba(46,125,50,0.3)" }}
        >
          <i className="mdi mdi-account-multiple-plus" style={{ fontSize: 16 }} />
          Join SHG
        </button>
      </div>

      {groupedShgMembers.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", padding: "40px 24px", textAlign: "center", color: "#A0AEC0" }}>
          <i className="mdi mdi-account-group-outline" style={{ fontSize: 36, display: "block", marginBottom: 10 }} />
          <div style={{ fontSize: 13 }}>No SHG members found for this JFMC type</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {groupedShgMembers.map((group, idx) => (
            <div key={group.shg?._id || idx} style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
              {/* SHG header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "#F7FAF7", borderBottom: "1px solid #EDF2F7" }}>
                <span style={{ width: 32, height: 32, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                  <i className="mdi mdi-account-group" />
                </span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>{group.shg?.name || "Unknown SHG"}</div>
                  <div style={{ fontSize: 11, color: "#718096" }}>{group.shg?.vfcId?.vfcName || ""}</div>
                </div>
                <span style={{ marginLeft: "auto", background: "#E8F5E9", color: "#1B5E20", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 50, border: "1px solid #A5D6A7" }}>
                  {group.members.length} member{group.members.length !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Members table */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#F7FAFC" }}>
                      {["#", "Member Name", "Designation", "Status", "Actions"].map((h) => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid #EDF2F7" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {group.members.map((sm, i) => (
                      <tr key={sm._id} style={{ background: i % 2 === 0 ? "#fff" : "#FAFAFA", borderBottom: "1px solid #F0F4F8" }}>
                        <td style={{ padding: "10px 14px", color: "#718096", fontWeight: 600 }}>{i + 1}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg,#1B5E20,#43A047)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                              {sm.member?.name?.charAt(0).toUpperCase()}
                            </span>
                            <span style={{ fontWeight: 600, color: "#1A202C" }}>{sm.member?.name || "N/A"}</span>
                          </div>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ background: "#E8F5E9", color: "#1B5E20", padding: "2px 10px", borderRadius: 50, fontWeight: 600, fontSize: 11, border: "1px solid #A5D6A7" }}>{sm.designation}</span>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ background: sm.status === "A" ? "#E8F5E9" : "#FFF5F5", color: sm.status === "A" ? "#1B5E20" : "#E53E3E", padding: "2px 10px", borderRadius: 50, fontWeight: 600, fontSize: 11 }}>
                            {sm.status === "A" ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button title="Edit" onClick={() => navigate(`/manage/shg/member/edit/${sm._id}`)} style={{ background: "#E8F5E9", border: "none", borderRadius: 6, padding: "4px 9px", cursor: "pointer", color: "#2E7D32" }}>
                              <i className="mdi mdi-pencil" style={{ fontSize: 14 }} />
                            </button>
                            <button title="Delete" onClick={() => deleteShgMembers(sm._id)} style={{ background: "#FFF5F5", border: "none", borderRadius: 6, padding: "4px 9px", cursor: "pointer", color: "#E53E3E" }}>
                              <i className="mdi mdi-delete" style={{ fontSize: 14 }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageShgMember;
